import { onUnmounted, readonly, ref, watch } from "vue"
import { useTokenManager } from "@webapp/auth/TokenManager"
import { Environment } from "@webapp/util/Environment"
import { HttpTransportType, HubConnection, HubConnectionBuilder } from '@microsoft/signalr'
import { SyncState } from "./ResearchDataModel"
import { useWorkspaceStore } from "./WorkspaceStore"
import { useSyncStore } from "./SyncStore"
import { LoadMode } from "@webapp/util/AsyncData"
import { useTelemetry } from "@webapp/util/Telemetry"
import { useGroupStore } from "./GroupStore"

const rdUrl = Environment.get('RESEARCH_DATA_URL')
let notificationHub: HubConnection | undefined;
const connectedRef = ref(false)

type Listener = (args: any) => void
const listenerSets = new Map<string, Set<Listener>>()

export function useResearchDataNotifications() {
    // NOTE: this is the only state that is local to this "use" call
    const localListeners: Listener[] = []

    // Capture telemetry here, in the composable's setup context. useAppInsights() uses Vue's
    // inject(), which returns undefined if called from initialize()/accessTokenChanged() since those
    // run from onMounted/watch callbacks, not setup. The closures below use these captured functions.
    const { trackEvent, trackException } = useTelemetry()

    function initialize() {
        if (notificationHub) {
            console.warn('ResearchDataNotifications already initialized')
            return
        }

        // NOTE: these will all be captured by closures below, so will have the same lifetime as the instance
        // on which this function was called
        const tokenManager = useTokenManager()
        const workspaceStore = useWorkspaceStore()
        const syncStore = useSyncStore()
        const groupStore = useGroupStore()

        notificationHub = new HubConnectionBuilder()
            .withUrl(`${rdUrl}notifications`, {
                transport: HttpTransportType.WebSockets | HttpTransportType.LongPolling,
                accessTokenFactory: async () => (await tokenManager.getAccessTokenAsync()) ?? '',
            })
            .withAutomaticReconnect()
            .build()

        notificationHub.onreconnecting(error => {
            connectedRef.value = false;
            trackEvent('ResearchDataNotifications.Dropped')
            if (error) {
                trackException(error)
            }
        })
        notificationHub.onreconnected(() => { 
            connectedRef.value = true;
            trackEvent('ResearchDataNotifications.Restored')
        })
        notificationHub.onclose(error => { 
            connectedRef.value = false;
            trackEvent('ResearchDataNotifications.Closed')
            if (error) {
                trackException(error)
            }
        })

        notificationHub.on('Group.DataChanged', (args: GroupDataChangedArgs) => {
            trackEvent('Group.DataChanged', args)
            raiseEvent('Group.DataChanged', args)
        })

        notificationHub.on('Group.ProcessingStateChanged', async (args: GroupProcessingStateChangedArgs) => {
            trackEvent('Group.ProcessingStateChanged', args)

            const asyncGroup = groupStore.asyncGroups.get(args.groupId)
            if (asyncGroup) {
                const groupIds = args.trackingGroupIds ?? [args.groupId]
                await groupStore.getGroupListAsync(groupIds, true)
            }
            raiseEvent('Group.ProcessingStateChanged', args)
        })

        notificationHub.on('Sync.Added', async (args: SyncAddedArgs) => {
            trackEvent('Sync.Added', args)

            // CONSIDER: reload only if already present?

            // this should load both the workspace (which may be new) and the last sync for the workspace,
            // which should be the one that was just added
            await workspaceStore.getWorkspacesForGroupsAsync([args.groupId], LoadMode.Reload)
            raiseEvent('Sync.Added', args)
        })

        notificationHub.on('Sync.StateChanged', async (args: SyncStateChangedArgs) => {
            trackEvent('Sync.StateChanged', args)

            const asyncSync = syncStore.asyncSyncs.get(args.itemId)
            if (asyncSync) {
                const newSync = await syncStore.getAsyncSync(args.itemId, LoadMode.Reload)?.whenLoadCompleted
                trackEvent('Sync.Reloaded', { syncID: args.itemId })
            }
            raiseEvent('Sync.StateChanged', args)
        })

        notificationHub.on('Sync.Progress', (args: SyncProgressArgs) => {
            // NOTE: don't track high-frequency notifications
            raiseEvent('Sync.Progress', args)
        })
        
        function raiseEvent<TArgs>(methodName: string, args: TArgs) {
            const listeners = listenerSets.get(methodName)
            if (listeners) {
                for (const ls of listeners) {
                    try {
                        ls(args)                    
                    }
                    catch (error) {
                        trackException(error) // track and continue
                    }
                }
            }
            listenerSets.get(methodName)?.forEach(ls => ls(args))
        }

        onUnmounted(() => {
            if (notificationHub) {
                notificationHub.stop()
                notificationHub = undefined
            }
        })    

        watch(() => tokenManager.current.userId, accessTokenChanged)

        trackEvent('ResearchDataNotifications.Initialized')

        accessTokenChanged()
    }

    function accessTokenChanged() {
        const tokenManager = useTokenManager()

        if (tokenManager.current.isValid && notificationHub?.state == 'Disconnected') {
            notificationHub.start()
                .then(() => { 
                    connectedRef.value = true
                    trackEvent('ResearchDataNotifications.Started')
                })
                .catch(err => trackException(err))
        }
        if (!tokenManager.current.isValid && notificationHub?.state == 'Connected') {
            notificationHub.stop()
                .then(() => { 
                    connectedRef.value = false
                    trackEvent('ResearchDataNotifications.Stopped')
                })
                .catch(err => trackException(err))
        }
    }

    const getConnectionId = () => notificationHub?.connectionId

    function addListener(methodName: string, listener: (args: any) => void) {
        if (!listenerSets.has(methodName)) {
            listenerSets.set(methodName, new Set())
        }
        listenerSets.get(methodName)!.add(listener)
        localListeners.push(listener)
    }

    function onGroupDataChanged(listener: (args: GroupDataChangedArgs) => void) {
        addListener('Group.DataChanged', listener)
    }

    function onSyncAdded(listener: (args: SyncStateChangedArgs) => void) {
        addListener('Sync.Added', listener)
    }

    function onSyncStateChanged(listener: (args: SyncStateChangedArgs) => void) {
        addListener('Sync.StateChanged', listener)
    }

    function onSyncProgress(listener: (args: SyncProgressArgs) => void) {
        addListener('Sync.Progress', listener)
    }

    onUnmounted(() => {
        // remove all local listeners
        localListeners.forEach(ls => {
            listenerSets.forEach((s: Set<Listener>) => {
                s.delete(ls) // if present
            })
        })
    })

    return {
        connectedRef: readonly(connectedRef),

        initialize,
        accessTokenChanged,
        getConnectionId,
        onGroupDataChanged,
        onSyncAdded,
        onSyncStateChanged,
        onSyncProgress,
    }
}

export interface GroupDataChangedArgs {
    groupId: string
    syncId: string
    trackingGroupIds: string[]
}

export interface GroupProcessingStateChangedArgs {
    groupId: string
    trackingGroupIds: string[]
}

export interface SyncAddedArgs {
    itemId: string
    groupId: string
}

export interface SyncStateChangedArgs {
    itemId: string
    state: SyncState
}

export interface SyncProgressArgs {
    syncID: string
    total: number
    completed: number
}
